/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { computed, action } from '@ember/object';
import { inject as service } from '@ember/service';
import CampaignModel from 'cio/models/campaign';
import MessageSettingsService from 'cio/services/message-settings';
import { getTimeFrameUnitsForMaxUnit, findRoundUnit } from 'cio/utils/round-timeframe-units';
import { MAX_MESSAGE_RETRY_WINDOW_SECS, DEFAULT_MESSAGE_RETRY_WINDOW_SECS } from 'cio/utils/message-auto-retry';
import { formatNumberAndPluralize } from 'cio/helpers/format-number-and-pluralize';

enum AutoRetrySettingsOption {
  useCampaignSettings = 'use_campaign_settings',
  doNotRetryMessage = 'do_not_retry_message',
  setCustomWindow = 'set_custom_window',
}

interface UseMessageLimitsArgs {
  actionAbstract: any;
  isSaving: boolean;
  hasReadOnlyPermission: boolean;
}

export default class UseMessageLimits extends Component<UseMessageLimitsArgs> {
  @service messageSettings!: MessageSettingsService;

  get actionModel() {
    return this.args.actionAbstract.action;
  }
  get campaign() {
    return this.actionModel.belongsTo('campaign').value() as CampaignModel | null;
  }

  get isVisible() {
    let { actionModel, campaign } = this;
    return (
      !(campaign?.isWebhookTriggered ?? false) &&
      (actionModel.info.showUnsubscribed || actionModel.isSplitTest || actionModel.type === 'webhook_action')
    );
  }

  get isDisabled() {
    return this.args.hasReadOnlyPermission || this.args.isSaving || !this.messageSettings.isMessagingLimitEnabled;
  }

  get isMessageLimitSelectDisabled() {
    return this.isDisabled || !this.messageSettings.isMessagingLimitEnabled;
  }

  @computed('args.actionAbstract.useMessageLimits')
  get isMessageCountedTowardsLimit() {
    if (!this.messageSettings.isMessagingLimitEnabled) {
      return false;
    }

    if (this.args.actionAbstract.useMessageLimits == null) {
      // 'null' is a valid option that means "Use campaign settings".
      return this.campaign?.useMessageLimits ?? false;
    }

    return this.args.actionAbstract.useMessageLimits === true;
  }

  @computed('isMessageLimitSelectDisabled', 'args.actionAbstract.useMessageLimits')
  get isAutoRetrySettingsDisabled() {
    return this.isMessageLimitSelectDisabled || !this.isMessageCountedTowardsLimit;
  }

  readonly messageLimitDisabledText =
    'You do not have any message limits set. If you want to limit the amount of messages sent, you can set a limit in your Workspace Settings.';

  get messageLimitTooltipContent() {
    if (!this.messageSettings.isMessagingLimitEnabled) {
      return this.messageLimitDisabledText;
    }

    return `Your campaign is set to ${
      this.campaign?.useMessageLimits ?? false ? '' : 'not '
    } count messages toward the message limit.`;
  }

  get retrySettingsTooltipContent() {
    if (!this.messageSettings.isMessagingLimitEnabled || !(this.campaign?.useMessageLimits ?? false)) {
      return this.messageLimitTooltipContent;
    }

    let seconds =
      this.campaign?.useWorkspaceMessageLimitsRetry ?? false
        ? this.messageSettings.messageLimit?.retryWindowSecs ?? 0
        : this.campaign?.messageLimitsRetryWindowSecs ?? 0;
    let unit = findRoundUnit(seconds, getTimeFrameUnitsForMaxUnit('hour'));
    let timeframe = Math.ceil(seconds / unit.seconds);

    return `Your campaign is set to ${
      seconds === 0 ? 'not retry messages' : `retry for ${formatNumberAndPluralize([timeframe, unit.value])}`
    }.`;
  }

  readonly useMessageLimitsStates = [
    {
      label: 'Use campaign settings',
      value: null,
    },
    {
      label: "Don't count toward limit",
      value: false,
    },
    {
      label: 'Count toward limit',
      value: true,
    },
  ];

  readonly maxMessageRetryWindowSecs = MAX_MESSAGE_RETRY_WINDOW_SECS;

  readonly autoRetrySettingsOptions = [
    {
      label: 'Use campaign settings',
      value: AutoRetrySettingsOption.useCampaignSettings,
    },
    {
      label: "Don't retry message",
      value: AutoRetrySettingsOption.doNotRetryMessage,
    },
    {
      label: 'Set a custom window',
      value: AutoRetrySettingsOption.setCustomWindow,
    },
  ];

  @computed('args.actionAbstract.{overrideMessageLimitsRetry,messageLimitsRetryWindowSecs}')
  get autoRetrySettingValue() {
    if (this.args.actionAbstract.overrideMessageLimitsRetry !== true) {
      return AutoRetrySettingsOption.useCampaignSettings;
    }

    if (this.args.actionAbstract.messageLimitsRetryWindowSecs > 0) {
      return AutoRetrySettingsOption.setCustomWindow;
    }

    return AutoRetrySettingsOption.doNotRetryMessage;
  }

  @action
  onUseMessageLimitStateChange({ value }: { value: boolean | null }) {
    this.args.actionAbstract.set('useMessageLimits', value);
  }

  @action
  onAutoRetrySettingChange({ value }: { label: string; value: string }) {
    if (value === AutoRetrySettingsOption.useCampaignSettings) {
      this.args.actionAbstract.set('overrideMessageLimitsRetry', false);
      return;
    }

    if (value === AutoRetrySettingsOption.doNotRetryMessage) {
      this.args.actionAbstract.set('overrideMessageLimitsRetry', true);
      this.args.actionAbstract.set('messageLimitsRetryWindowSecs', 0);
      return;
    }

    if (value === AutoRetrySettingsOption.setCustomWindow) {
      this.args.actionAbstract.set('overrideMessageLimitsRetry', true);
      let seconds = this.args.actionAbstract.messageLimitsRetryWindowSecs ?? 0;
      if (seconds === 0) {
        seconds = DEFAULT_MESSAGE_RETRY_WINDOW_SECS;
      }
      this.args.actionAbstract.set('messageLimitsRetryWindowSecs', seconds);
    }
  }

  @action
  onActionMessageLimitRetryWindowSecsChange(seconds: number) {
    this.args.actionAbstract.set('messageLimitsRetryWindowSecs', seconds);
  }
}
